// src/components/profile/ProfileAvatar.jsx
import { useRef, useState } from "react";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import AvatarEditor from "react-avatar-editor";

import styles from "../../styles/components/profile/ProfileAvatar.module.css";
import { FaCamera } from "react-icons/fa";
import { MAX_IMAGE_UPLOAD_BYTES } from "../../constants/uploadLimits";

export default function ProfileAvatar({ user, onUpdated, className = "", imageClassName = "" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const editorRef = useRef(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [zoom, setZoom] = useState(1.2);
  const [rotation, setRotation] = useState(0);

  const handlePick = () => fileRef.current?.click();

  const uploadAvatar = async (blob) => {
    const form = new FormData();
    const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
    form.append("avatar", file);
    try {
      setUploading(true);
      const res = await api.post("/users/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated?.(res.data.avatar);
      setCropOpen(false);
      setSelectedFile(null);
      setZoom(1.2);
      setRotation(0);
    } catch (err) {
      console.error("Avatar upload failed", err);
      alert(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      alert("Please select an image file.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      alert("Image must be smaller than 5MB.");
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
    setZoom(1.2);
    setRotation(0);
    setCropOpen(true);
    e.target.value = "";
  };

  const handleCropCancel = () => {
    setCropOpen(false);
    setSelectedFile(null);
    setZoom(1.2);
    setRotation(0);
  };

  const handleCropSave = () => {
    if (!editorRef.current) return;

    // Get the cropped canvas
    const canvas = editorRef.current.getImageScaledToCanvas();
    canvas.toBlob(
      (blob) => {
        if (blob) {
          uploadAvatar(blob);
        }
      },
      "image/jpeg",
      0.92,
    );
  };

  const src = getImageUrl(user?.avatar);

  return (
    <div className={`${styles.preview} ${className}`.trim()}>
      <div className={styles.avatarRing}>
        <img
          src={src}
          alt="Avatar"
          className={imageClassName || styles.avatarImage}
        />
      </div>

      {uploading && (
        <div className={styles.uploadingOverlay}>
          <div className={styles.spinner} />
          <span className={styles.uploadingText}>Uploading...</span>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={handleChange}
        className={styles.hiddenInput}
      />
      <button
        type="button"
        className={styles.editBtn}
        onClick={handlePick}
        disabled={uploading}
        title="Change photo"
      >
        <FaCamera className={styles.cameraIcon} />
      </button>

      {cropOpen && selectedFile && (
        <div className={styles.cropOverlay} onClick={handleCropCancel}>
          <div
            className={styles.cropModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.cropHeader}>
              <h3 className={styles.cropTitle}>Adjust your photo</h3>
              <p className={styles.cropSubtitle}>
                Drag to reposition. Use the slider to zoom.
              </p>
            </div>
            <div className={styles.editorWrapper}>
              <AvatarEditor
                ref={editorRef}
                image={selectedFile}
                width={220}
                height={220}
                border={40}
                borderRadius={110}
                color={[15, 23, 42, 0.7]}
                scale={zoom}
                rotate={rotation}
                crossOrigin="anonymous"
              />
            </div>
            <div className={styles.zoomRow}>
              <span className={styles.zoomLabel}>Zoom</span>
              <input
                type="range"
                min={1}
                max={4}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className={styles.zoomSlider}
              />
            </div>
            <div className={styles.zoomRow}>
              <span className={styles.zoomLabel}>Rotate</span>
              <input
                type="range"
                min={0}
                max={360}
                step={1}
                value={rotation}
                onChange={(e) => setRotation(Number(e.target.value))}
                className={styles.zoomSlider}
              />
            </div>
            <div className={styles.cropActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={handleCropCancel}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleCropSave}
                disabled={uploading}
              >
                {uploading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
